import { ScaleElement } from '@scale/base-element';

const GESTURES = ['touchend', 'pointerup', 'click', 'keydown'];

class ScaleDeferredMedia extends ScaleElement {
  #preload;
  #sight;
  #gesture;
  #resume = true;

  setup() {
    const playBtn = this.ref('play');
    if (playBtn) {
      this.on('click', () => this.#playOnClick(), { target: playBtn, once: true });
    }

    if (!this.getBoolean('data-autoplay') || matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#preload = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.#preload.disconnect();
      if (this.ref('media').content.querySelector('video')) this.#load();
    }, { rootMargin: '200px' });
    this.#preload.observe(this);

    this.#sight = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.#enter();
      else this.#leave();
    });
    this.#sight.observe(this);
  }

  teardown() {
    this.#preload?.disconnect();
    this.#sight?.disconnect();
    this.#gesture?.abort();
  }

  #load() {
    const template = this.ref('media');
    if (!template || this.hasAttribute('loaded')) return;

    template.parentElement.appendChild(template.content.cloneNode(true));
    this.setAttribute('loaded', '');

    const video = this.querySelector('video');
    if (!video) {
      this.setAttribute('data-media-shown', '');
      return;
    }
    this.on('playing', () => this.setAttribute('data-media-shown', ''), { target: video, once: true });
    if (this.getBoolean('data-autoplay')) {
      video.muted = true;
      video.playsInline = true;
    }
  }

  #playOnClick() {
    this.#load();
    const video = this.querySelector('video');
    if (video) this.#play(video);
  }

  #enter() {
    this.#load();
    const video = this.querySelector('video');
    if (!video) {
      this.#sight.disconnect();
      return;
    }
    if (!this.#resume) return;

    this.#resume = false;
    this.#play(video);
  }

  #leave() {
    const video = this.querySelector('video');
    if (!video || video.paused) return;

    video.pause();
    this.#resume = true;
  }

  #play(video) {
    video.play().then(() => this.removeAttribute('data-autoplay-blocked'), (error) => {
      if (error.name === 'AbortError') return;
      if (error.name !== 'NotAllowedError') throw error;
      this.setAttribute('data-autoplay-blocked', '');
      this.#retryOnGesture(video);
    });
  }

  #retryOnGesture(video) {
    this.#gesture?.abort();
    this.#gesture = new AbortController();
    const retry = () => {
      this.#gesture.abort();
      this.#play(video);
    };
    for (const type of GESTURES) {
      document.addEventListener(type, retry, { capture: true, passive: true, signal: this.#gesture.signal });
    }
  }
}

if (!customElements.get('scale-deferred-media')) {
  customElements.define('scale-deferred-media', ScaleDeferredMedia);
}
